<template>
  <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 py-8 px-2 md:px-0">
    <!-- Left Content: Artist Profile
    <div class="order-1 md:order-1 md:col-span-8"> -->
      <!-- Left Content: Artist Profile -->
<div class="order-1 md:order-1 md:col-span-8 overflow-y-auto md:h-[160vh] pr-2">

      <div class="bg-white p-6 rounded shadow h-auto space-y-10 gsap-artist-detail">
        <!-- Top Section -->
        <div class="flex flex-col md:flex-row md:items-start md:space-x-8 space-y-6 md:space-y-0 gsap-header">
          <div class="md:w-2/3 space-y-2 mt-6 text-left">
            <h1 class="text-3xl font-bold text-red-600">{{ artist.name }}</h1>
            <h2 v-if="artist.profession" class="text-lg text-gray-500">{{ artist.profession }}</h2>
            <p v-if="artist.quote" class="text-md text-gray-600 italic mt-2 text-center md:text-left">"{{ artist.quote }}"</p>
            <p v-if="artist.dob" class="text-sm text-gray-400 mt-1">Born: {{ new Date(artist.dob).toLocaleDateString() }}</p>
            <div class="w-1/2 h-[2px] bg-gray-300 mt-2"></div>


          </div>

          <div class="md:w-1/3 flex justify-center md:justify-end">
            <img
              :src="artist.image"
              :alt="artist.name"
              class="w-full sm:w-[240px] h-[180px] object-cover rounded-md shadow-md gsap-image"
            />
          </div>
        </div>

        <!-- Description -->
        <div class="gsap-desc">
          <p class="text-gray-600 text-base leading-relaxed text-justify indent-8">
            {{ artist.description }}
          </p>
        </div>

        <!-- Achievements -->
        <div v-if="artist.achievements">
          <h3 class="text-lg font-semibold text-[#D84040] mb-2">Achievements</h3>
          <ul class="list-disc list-inside space-y-2 text-gray-600 text-base leading-relaxed">
            <li v-for="(achievement, index) in formatAchievements(artist.achievements)" :key="index">
              {{ achievement }}
            </li>
          </ul>
        </div>

        <!-- Gallery -->
        <section class="gsap-gallery">
          <h2 class="text-2xl font-semibold text-gray-800 mb-4 mt-8">Gallery</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 place-items-center">
            <img
              v-for="(galleryImage, index) in artist.galleryImages"
              :key="index"
              :src="galleryImage"
              :alt="`Gallery ${index + 1}`"
              class="w-full h-[220px] object-cover rounded shadow"
            />
          </div>
        </section>

        <!-- Social Links (Moved) -->
        <div v-if="artist.links && artist.links.length" class="mt-8 pt-6 border-t border-gray-100">
          <h3 class="text-lg font-semibold text-[#D84040] mb-4">Connect</h3>
          <div class="flex flex-wrap gap-3">
            <a 
              v-for="(link, index) in artist.links" 
              :key="index" 
              :href="ensureUrl(link.link_url)" 
              target="_blank" 
              rel="noopener noreferrer"
              class="inline-flex items-center px-5 py-2 bg-white text-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all border border-gray-200 group"
            >
              <span class="font-medium">{{ link.link_type }}</span>
              <span class="ml-2 text-[#D84040] group-hover:translate-x-1 transition-transform">→</span>
            </a>
          </div>
        </div>

        <!-- Social Interaction Section -->
        <div class="mt-8 pt-6 border-t border-gray-100">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-[#D84040]">Comments & Likes</h3>
            
            <!-- Like Button -->
            <button 
              @click="toggleLike"
              class="flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 transform hover:scale-105"
              :class="userLiked ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" :fill="userLiked ? 'currentColor' : 'none'" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span class="font-bold text-lg">{{ likesCount }}</span>
            </button>
          </div>

          <!-- Comments List -->
          <div class="space-y-4 mb-8 max-h-[400px] overflow-y-auto pr-2">
            <div v-if="comments.length === 0" class="text-center py-8 text-gray-500 italic bg-gray-50 rounded-lg">
              No comments yet. Be the first to share your thoughts!
            </div>
            
            <div v-for="comment in comments" :key="comment.id" class="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <div class="flex justify-between items-start mb-2">
                <span class="font-semibold text-gray-800">{{ comment.user_name || 'Anonymous' }}</span>
                <span class="text-xs text-gray-400">{{ new Date(comment.created_at).toLocaleDateString() }}</span>
              </div>
              <p class="text-gray-700 text-sm whitespace-pre-wrap">{{ comment.comment }}</p>
            </div>
          </div>

          <!-- Add Comment Form -->
          <div v-if="currentUser" class="bg-white rounded-lg">
            <h4 class="text-sm font-semibold text-gray-700 mb-2">Leave a comment</h4>
            <div class="flex gap-2">
              <textarea 
                v-model="newComment"
                rows="2"
                placeholder="Write your appreciation here..." 
                class="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#D84040] focus:border-transparent outline-none resize-none text-sm"
              ></textarea>
              <button 
                @click="submitComment"
                :disabled="!newComment.trim() || isSubmitting"
                class="bg-[#BF3131] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#a62b2b] disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-end"
              >
                Send
              </button>
            </div>
          </div>
          <div v-else class="text-center py-4 bg-gray-50 rounded-lg">
            <router-link to="/login" class="text-[#BF3131] font-semibold hover:underline">Log in</router-link> to like and comment.
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar -->
    <div class="order-2 md:order-2 md:col-span-4">
      <RightSidebar />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import Swal from 'sweetalert2';
import RightSidebar from "../components/RightsideBar.vue";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

// ENV - keep your existing defaults
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || "https://sanskritirdapoon.in/api";
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL || "https://sanskritirdapoon.in";

// reactive artist state
const artist = ref({
  name: "",
  profession: "",
  dob: "",
  quote: "",
  description: "",
  achievements: "",
  image: `${FILE_BASE_URL}/images/default.png`,
  galleryImages: [],
  links: [],
});

// social state
const likesCount = ref(0);
const userLiked = ref(false);
const comments = ref([]);
const newComment = ref("");
const currentUser = ref(null);
const isSubmitting = ref(false);

// route
const route = useRoute();

// parse achievements
function formatAchievements(achievements) {
  if (!achievements) return [];
  return achievements
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

// helper to ensure valid url
function ensureUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
}

// fetch artist by id
async function fetchArtist() {
  const id = route.params.id;
  if (!id) return;

  // reset
  artist.value = {
    name: "",
    profession: "",
    description: "",
    achievements: "",
    image: `${FILE_BASE_URL}/images/default.png`,
    galleryImages: [],
    links: [],
  };

  // scroll to top
  window.scrollTo(0, 0);

  try {
    const res = await fetch(`${API_BASE_URL}/artists/${id}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    artist.value = {
      ...data,
      image: data.image
        ? (data.image.startsWith("data:") || data.image.startsWith("http") ? data.image : `${FILE_BASE_URL}${data.image}`)
        : `${FILE_BASE_URL}/images/default.png`,
      galleryImages:
        data.galleryImages && data.galleryImages.length
          ? data.galleryImages.map(img => (img.startsWith("data:") || img.startsWith("http") ? img : `${FILE_BASE_URL}${img}`))
          : [],
    };

    // wait DOM update then animate
    await nextTick();
    animateDetail();
    
    // Fetch social data after artist is loaded
    fetchSocialData(id);
    
  } catch (err) {
    console.error("Error fetching artist:", err);
  }
}

// fetch social data
async function fetchSocialData(artistId) {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    currentUser.value = user;
    const userIdQuery = user ? `?user_id=${user.id}` : '';
    
    const res = await fetch(`${API_BASE_URL}/artists/${artistId}/social${userIdQuery}`);
    if (res.ok) {
      const data = await res.json();
      likesCount.value = data.likes;
      userLiked.value = data.user_liked;
      comments.value = data.comments;
    }
  } catch (err) {
    console.error("Error fetching social data:", err);
  }
}

// toggle like
async function toggleLike() {
  if (!currentUser.value) {
    Swal.fire({
      icon: 'warning',
      title: 'Login Required',
      text: 'Please log in to like this artist.',
      confirmButtonColor: '#BF3131'
    });
    return;
  }
  
  const originalLiked = userLiked.value;
  const originalCount = likesCount.value;
  
  // Optimistic UI update
  userLiked.value = !userLiked.value;
  likesCount.value += userLiked.value ? 1 : -1;
  
  try {
    const res = await fetch(`${API_BASE_URL}/artists/${artist.value.id}/like`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({}) // Backend uses token for user_id
    });
    
    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || "Failed to toggle like");
    }
    
  } catch (err) {
    // Revert on error
    userLiked.value = originalLiked;
    likesCount.value = originalCount;
    Swal.fire({
      icon: 'error',
      title: 'Action Failed',
      text: 'Action failed. Please try again.',
      confirmButtonColor: '#BF3131'
    });
  }
}

// submit comment
async function submitComment() {
  if (!currentUser.value || !newComment.value.trim()) return;
  
  isSubmitting.value = true;
  try {
    const res = await fetch(`${API_BASE_URL}/artists/${artist.value.id}/comments`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify({ 
        comment: newComment.value
      })
    });
    
    if (res.ok) {
      // Refresh comments
      fetchSocialData(artist.value.id);
      newComment.value = "";
    } else {
      const errorData = await res.json();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorData.error || 'Failed to post comment.',
        confirmButtonColor: '#BF3131'
      });
    }
  } catch (err) {
    console.error("Error posting comment:", err);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Error posting comment.',
      confirmButtonColor: '#BF3131'
    });
  } finally {
    isSubmitting.value = false;
  }
}

// GSAP animation - kills previous triggers to avoid duplication
function animateDetail() {
  // kill previous ScrollTriggers if any
  ScrollTrigger.getAll().forEach(t => t.kill());

  // overall fade-in
  gsap.from(".gsap-artist-detail", { opacity: 0, y: 30, duration: 0.6, ease: "power2.out" });

  // header elements
  gsap.from(".gsap-header", { opacity: 0, y: 12, duration: 0.6, delay: 0.1, stagger: 0.08 });

  // image pop
  gsap.from(".gsap-image", { opacity: 0, scale: 0.98, duration: 0.7, delay: 0.15 });

  // description reveal on scroll (if long)
  gsap.from(".gsap-desc", {
    scrollTrigger: { trigger: ".gsap-desc", start: "top 90%", toggleActions: "play none none none" },
    opacity: 0,
    y: 20,
    duration: 0.8,
  });

  // gallery stagger
  gsap.from(".gsap-gallery img", {
    scrollTrigger: { trigger: ".gsap-gallery", start: "top 85%", toggleActions: "play none none none" },
    opacity: 0,
    y: 30,
    duration: 0.8,
    stagger: 0.12,
  });
}

// initial mount
onMounted(() => {
  fetchArtist();
});

// watch route param id changes and re-fetch
watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      fetchArtist();
    }
  }
);
</script>